import type { LucideIcon } from 'lucide-react';
import { TerminalSquare, FileCode, FolderKanban, Monitor, Globe, ClipboardList, MessagesSquare, Play, FileBox } from 'lucide-react';
import type { Project } from '../types/tauri';
import { RDP_ENABLED } from '../config/features';
import FilesTab from '../components/workspace/FilesTab';
import ProjectsPanel from '../components/workspace/ProjectsPanel';
import RdpViewer from '../components/rdp/RdpViewer';
import TerminalTab from '../components/workspace/TerminalTab';
import BrowserTab from '../components/workspace/BrowserTab';
import ChatPanel from '../components/workspace/ChatPanel';
import QueueTab from '../components/workspace/QueueTab';
import RunnersPanel from '../components/workspace/RunnersPanel';
import ClaudeArtifactsPanel from '../components/workspace/ClaudeArtifactsPanel';

export interface WorkspaceContext {
  projectId?: string;
  project?: Project | null;
}

export interface TabDefinition {
  id: string;
  displayName: string;
  icon: LucideIcon;
  component: React.ComponentType<any>;
  propsFactory: (ctx: WorkspaceContext) => Record<string, unknown>;
  border: boolean;
  addable: boolean;
  mobileOrder: number | null;
}

const registry = new Map<string, TabDefinition>();

function registerTab(def: TabDefinition) {
  registry.set(def.id, def);
}

export function getTab(id: string | undefined): TabDefinition | undefined {
  if (!id) return undefined;
  return registry.get(id);
}

export function getAddableTabs(): TabDefinition[] {
  return Array.from(registry.values()).filter((t) => t.addable);
}

export function getMobileTabs(): TabDefinition[] {
  return Array.from(registry.values())
    .filter((t) => t.mobileOrder !== null)
    .sort((a, b) => a.mobileOrder! - b.mobileOrder!);
}

// --- Register built-in tabs ---

registerTab({
  id: 'terminal',
  displayName: 'Terminal',
  icon: TerminalSquare,
  component: TerminalTab,
  propsFactory: (ctx) => ({ projectPath: ctx.project?.root_path, agentType: ctx.project?.agent_type }),
  border: false,
  addable: true,
  mobileOrder: 0,
});

registerTab({
  id: 'files',
  displayName: 'Files',
  icon: FileCode,
  component: FilesTab,
  propsFactory: (ctx) => ({ projectId: ctx.projectId }),
  border: false,
  addable: true,
  mobileOrder: 1,
});

registerTab({
  id: 'projects',
  displayName: 'Projects',
  icon: FolderKanban,
  component: ProjectsPanel,
  propsFactory: () => ({}),
  border: true,
  addable: false,
  mobileOrder: null,
});

registerTab({
  id: 'rdp',
  displayName: 'Remote Desktop',
  icon: Monitor,
  component: RdpViewer,
  propsFactory: () => ({}),
  border: false,
  addable: RDP_ENABLED, // hidden from the Add Panel menu until RDP is ready to ship
  mobileOrder: null,
});

registerTab({
  id: 'browser',
  displayName: 'Browser',
  icon: Globe,
  component: BrowserTab,
  propsFactory: (ctx) => ({ projectId: ctx.projectId, projectPath: ctx.project?.root_path }),
  border: false,
  addable: true,
  mobileOrder: 2,
});

registerTab({
  id: 'queue',
  displayName: 'Queue',
  icon: ClipboardList,
  component: QueueTab,
  propsFactory: (ctx) => ({ projectId: ctx.projectId }),
  border: false,
  addable: true,
  mobileOrder: 3,
});

registerTab({
  id: 'claude-artifacts',
  displayName: 'Claude Artifacts',
  icon: FileBox,
  component: ClaudeArtifactsPanel,
  propsFactory: (ctx) => ({ projectId: ctx.projectId }),
  border: false,
  addable: true,
  mobileOrder: null,
});

registerTab({
  id: 'runners',
  displayName: 'Runners',
  icon: Play,
  component: RunnersPanel,
  propsFactory: (ctx) => ({ projectPath: ctx.project?.root_path }),
  border: false,
  addable: true,
  mobileOrder: null,
});

// --- Unified AI chat hub ---------------------------------------------------
//
// ONE floating, project-independent "Chat" panel holds every AI chat as a tab
// (presets: Claude/ChatGPT/Gemini/Perplexity/Grok + any custom URL). Tab config
// lives in the global chatStore (persisted, never per-project), and each tab is
// backed by a persistent CEF host browser (label `chat-<id>`) so logins stick
// across restarts and switching projects never reloads a chat. Replaces the old
// separate chat-claude/chat-chatgpt/chat-gemini panels.
registerTab({
  id: 'chat',
  displayName: 'Chat',
  icon: MessagesSquare,
  component: ChatPanel,
  propsFactory: () => ({}),
  border: false,
  addable: true,
  mobileOrder: null,
});

// Back-compat: saved layouts may still contain the old per-service chat panels.
// Resolve each to the unified Chat panel focused on that service so upgraded
// layouts don't break. Not addable — the single "Chat" entry above replaces
// them in the Add Panel menu.
for (const service of ['claude', 'chatgpt', 'gemini'] as const) {
  registerTab({
    id: `chat-${service}`,
    displayName: 'Chat',
    icon: MessagesSquare,
    component: ChatPanel,
    propsFactory: () => ({ initialService: service }),
    border: false,
    addable: false,
    mobileOrder: null,
  });
}
